<!-- Top toolbar removed; actions will live next to the filter and search -->

<mat-card class="cardPlain">
    <mat-card-header>
        <div class="section-header d-flex flex-col gap-4">
            <app-breadcrumbs [items]="breadcrumbs"></app-breadcrumbs>
            <h1 class="m-0 f-w-500">Webinars</h1>
            <p class="m-0 help-text">
                Manage all your webinars in one place — track live sessions, upcoming events, drafts, and completed
                webinars with ease.
            </p>
        </div>
        <span class="spacer"></span>
    </mat-card-header>

    <mat-card-content>

        <div class="filter-bar">
            <div class="filter-controls">
                <!-- Re-added using the same plain Material structure as in Settings -->
                <div class="status-select">
                    <mat-form-field appearance="outline" class="w-100">
                        <mat-icon matPrefix>filter_list</mat-icon>
                        <mat-label>Filter</mat-label>
                        <mat-select
                                [value]="statusFilter"
                                (valueChange)="onStatusSelectChange($event)"
                                multiple
                                [panelClass]="'webinars-status-panel'">
                            <!-- Custom trigger showing selected statuses as pills (without icons) -->
                            <mat-select-trigger>
                <span class="select-trigger-pills">
                  @if (!statusFilter || statusFilter.length === 0) {
                      <span class="status-pill status-all filter-pill">All</span>
                  }
                    @if (statusFilter && statusFilter.length) {
                        @for (v of statusFilter; track v) {
                            <span [ngClass]="'status-' + v" class="status-pill filter-pill">
                        {{ statusMetaFor(v).label }}
                      </span>
                        }
                    }
                </span>
                            </mat-select-trigger>

                            <!-- Dropdown options: show pills without icons, include amount inside the pill -->
                            @for (filter of statusFilters; track filter) {
                                <mat-option [value]="filter.value">
                  <span [ngClass]="'status-' + filter.value" class="status-pill">
                    {{ statusMetaFor(filter.value).label }}
                      <span>({{ statusCounts[filter.value] || 0 }})</span>
                  </span>
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="search-field">
                    <mat-form-field appearance="outline" class="w-100">
                        <mat-icon matPrefix>search</mat-icon>
                        <mat-label>Search</mat-label>
                        <input (input)="onSearch($event)" [value]="filterTerm" matInput type="text"/>
                    </mat-form-field>
                </div>

                <!-- Actions placed in the same row as filter and search -->
                <div class="filter-actions">
                    <button
                            class="input-like-btn refresh-btn"
                            type="button"
                            (click)="onRefreshClick()"
                            aria-label="Refresh webinars"
                            mat-stroked-button
                            color="primary">
                        <mat-icon>refresh</mat-icon>
                        <span>Refresh</span>
                    </button>
                    <button
                            class="input-like-btn new-webinar-btn"
                            type="button"
                            (click)="create.emit()"
                            mat-flat-button
                            color="primary">
                        <mat-icon>add</mat-icon>
                        <span>New webinar</span>
                    </button>
                </div>
            </div>
        </div>

        @if (series.length || seriesLoadErrorMessage) {
            <section class="series-section d-flex flex-col gap-12">
                <div class="section-header d-flex flex-col gap-4">
                    <h2 class="m-0 f-s-18 f-w-600">Recurring series</h2>
                    <p class="m-0 help-text">Open a series to review generated occurrences and stored timezone.</p>
                </div>
                @if (seriesLoadErrorMessage) {
                    <div aria-live="polite" class="series-error-state" role="status">
                        <mat-icon aria-hidden="true">error_outline</mat-icon>
                        <div>
                            <h3 class="m-0 f-s-14 f-w-600">Recurring series unavailable</h3>
                            <p class="m-0">{{ seriesLoadErrorMessage }} One-time webinars are still available below.</p>
                        </div>
                    </div>
                } @else {
                    <div class="table-responsive">
                        <table [dataSource]="series" class="webinars-table" mat-table>
                            <ng-container matColumnDef="title">
                                <th *matHeaderCellDef mat-header-cell>Series</th>
                                <td *matCellDef="let item" mat-cell>
                                    <div class="title-cell">
                                        <span class="title">{{ item.title }}</span>
                                        <span class="f-s-12 text-gray-100">{{ item.slug }}</span>
                                    </div>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="recurrence">
                                <th *matHeaderCellDef mat-header-cell>Rule</th>
                                <td *matCellDef="let item" mat-cell>{{ formatSeriesRule(item) }}</td>
                            </ng-container>

                            <ng-container matColumnDef="nextOccurrence">
                                <th *matHeaderCellDef mat-header-cell>Next occurrence</th>
                                <td *matCellDef="let item" mat-cell>
                                    @if (item.nextOccurrence) {
                                        <span>{{ formatDateOnly(item.nextOccurrence.scheduledAt) }}</span>
                                        <span class="text-gray-100"> {{ formatTimeRange(item.nextOccurrence.scheduledAt, item.nextOccurrence.endAt) }}</span>
                                    } @else {
                                        <span class="text-gray-100">None scheduled</span>
                                    }
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="timezone">
                                <th *matHeaderCellDef mat-header-cell>Timezone</th>
                                <td *matCellDef="let item" mat-cell>{{ item.timezone }}</td>
                            </ng-container>

                            <ng-container matColumnDef="occurrences">
                                <th *matHeaderCellDef mat-header-cell>Occurrences</th>
                                <td *matCellDef="let item" mat-cell>{{ item.occurrenceCount || 0 }}</td>
                            </ng-container>

                            <tr *matHeaderRowDef="seriesDisplayedColumns" mat-header-row></tr>
                            <tr
                                    (click)="selectSeries(item)"
                                    *matRowDef="let item; columns: seriesDisplayedColumns"
                                    mat-row
                            ></tr>
                        </table>
                    </div>
                }
            </section>
        }

        <div
                aria-label="Scrollable webinars table"
                class="table-responsive webinars-one-time-table-wrapper"
                data-testid="webinars-one-time-table-wrapper"
                role="region"
                tabindex="0">
            <table [class.is-empty]="!dataSource.data.length" [dataSource]="dataSource" class="webinars-table"
                   mat-table matSort>
                <ng-container matColumnDef="title">
                    <th *matHeaderCellDef mat-header-cell>Title</th>
                    <td *matCellDef="let webinar" mat-cell>
                        <div class="title-cell">
                            <span class="title">{{ webinar.title }}</span>
                        </div>
                    </td>
                </ng-container>

                <!-- Split schedule into separate Date and Time columns -->
                <ng-container matColumnDef="scheduleDate">
                    <th *matHeaderCellDef mat-header-cell mat-sort-header sortActionDescription="Sort by schedule date">
                        Schedule Date
                    </th>
                    <td *matCellDef="let webinar" mat-cell>
                        {{ formatDateOnly(webinar.scheduledAt) }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="time">
                    <th *matHeaderCellDef mat-header-cell>Time</th>
                    <td *matCellDef="let webinar" mat-cell>
                        {{ formatTimeRange(webinar.scheduledAt, webinar.endAt) }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="provider">
                    <th *matHeaderCellDef mat-header-cell>Provider</th>
                    <td *matCellDef="let webinar" mat-cell>{{ webinar.provider | titlecase }}</td>
                </ng-container>

                <ng-container matColumnDef="liveViewers">
                    <th *matHeaderCellDef mat-header-cell>Live viewers</th>
                    <td *matCellDef="let webinar" mat-cell class="live-viewers-cell">
                        {{ webinar.liveViewers || 0 }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="status">
                    <th *matHeaderCellDef mat-header-cell>Status</th>
                    <td *matCellDef="let webinar" mat-cell>
            <span [ngClass]="'status-' + webinar.status" class="status-pill">
              <mat-icon>{{ statusMetaFor(webinar.status).icon }}</mat-icon>
                {{ statusMetaFor(webinar.status).label }}
            </span>
                    </td>
                </ng-container>

                <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
                <tr
                        (click)="selectRow(webinar)"
                        *matRowDef="let webinar; columns: displayedColumns"
                        [class.selected]="webinar.id === selectedWebinarId"
                        mat-row
                ></tr>

                <!-- Table footer row: single cell that spans all columns and contains the total + paginator -->
                <ng-container matColumnDef="footer">
                    <td
                            *matFooterCellDef
                            [attr.colspan]="displayedColumns.length"
                            class="table-footer-cell"
                            mat-footer-cell
                    >
                        <div class="table-footer-content">
                            <div class="total-count">Total <span class="amount">{{ dataSource.data.length }}</span>
                            </div>
                            <mat-paginator
                                    [length]="dataSource.data.length"
                                    [pageSize]="10"
                                    [hidePageSize]="true"
                                    [showFirstLastButtons]="true"
                            ></mat-paginator>
                        </div>
                    </td>
                </ng-container>

                <tr mat-footer-row *matFooterRowDef="['footer']" [hidden]="!dataSource.data.length"></tr>
            </table>
        </div>

        @if (!dataSource.data.length) {
            <div aria-live="polite" class="empty-state" role="region">
                <h3 class="title">{{ hasActiveFilters ? 'No results' : 'No webinars yet' }}</h3>
                <p class="description">
                    {{
                        hasActiveFilters
                            ? 'Try adjusting or clearing your filters to see more webinars.'
                            : 'Get started by creating your first webinar.'
                    }}
                </p>
                <div class="actions">
                    <button (click)="create.emit()" color="primary" mat-flat-button type="button">
                        Create webinar
                    </button>
                    @if (hasActiveFilters) {
                        <button (click)="onClearFiltersClick()" color="primary" mat-stroked-button type="button">
                            Clear filters
                        </button>
                    }
                </div>
            </div>
        }
    </mat-card-content>
</mat-card>

<!-- Bottom floating action removed; action is now in filter row -->
